import {
  capitalizeFirst,
  lowerFirst,
  properWords,
  stripEnd,
} from "@/lib/tool-kit/essay/sentence";

/**
 * A curated bank of academic sentence starters, grouped by what the sentence does.
 *
 * No starter makes the topic the subject of a verb that has to agree with it, so a
 * plural topic ("electric cars") reads as well as a singular one ("remote learning").
 *
 * `{topic}` marks a slot the reader's topic can fill; `___` marks a blank to complete
 * by hand; a closing `…` means the sentence continues in the writer's own words. The
 * bank is the English default — the widget exposes it through its strings so a
 * translated page can supply its own.
 */

export const STARTER_FUNCTIONS = [
  "introducing",
  "background",
  "thesis",
  "evidence",
  "explaining",
  "comparing",
  "conceding",
  "emphasizing",
  "methods",
  "results",
  "limitations",
  "concluding",
] as const;

export type StarterFunction = (typeof STARTER_FUNCTIONS)[number];

export const SENTENCE_STARTERS: Record<StarterFunction, readonly string[]> = {
  introducing: [
    "Few questions in ___ have drawn as much attention as {topic}.",
    "The debate over {topic} has moved from the margins to the center of …",
    "At first glance, {topic} may seem straightforward, but …",
    "This essay examines {topic}, focusing on …",
    "Discussions of {topic} often turn on a single question: …",
    "For all the attention paid to {topic}, it is rarely …",
    "Any account of {topic} has to begin with …",
    "The question of {topic} is not new; …",
    "Interest in {topic} has grown in recent years, largely because …",
    "Public debate about {topic} tends to overlook …",
    "To understand {topic}, it helps to consider …",
    "Any serious discussion of {topic} raises difficult questions about …",
    "Scholars have approached {topic} from several directions, including …",
    "What makes {topic} worth studying is …",
    "This paper sets out to explain why {topic} …",
    "In recent decades, the question of {topic} has taken on new importance because …",
    "It is often assumed that {topic} …; this essay asks whether …",
  ],
  background: [
    "Before ___, the standard view of {topic} was that …",
    "The origins of {topic} can be traced to …",
    "Historically, thinking about {topic} has been shaped by …",
    "For most of the twentieth century, …",
    "Early research on {topic} focused mainly on …",
    "The term “___” was first used to describe …",
    "Until recently, it was widely assumed that …",
    "The context for this debate is …",
    "Since the publication of ___, researchers have …",
    "Several developments explain the recent attention to {topic}, including …",
    "A common definition of {topic} is …",
    "A brief history of {topic} shows that …",
    "The current situation is the result of …",
    "Earlier studies established that …",
    "In the years leading up to ___, …",
    "To place {topic} in context, it is worth noting that …",
    "Policy on {topic} changed significantly when …",
  ],
  thesis: [
    "This essay argues that {topic} …",
    "The central claim of this paper is that …",
    "I will argue that, despite ___, …",
    "Contrary to the common view that ___, this essay contends that …",
    "The evidence suggests that the best way to understand {topic} is as …",
    "This paper makes the case that …",
    "Rather than ___, {topic} should be seen as …",
    "Although ___, the stronger position is that …",
    "This essay maintains that the main cause of ___ is …",
    "Ultimately, what happens with {topic} depends less on ___ than on …",
    "The aim of this essay is to show that …",
    "Three factors explain {topic}: ___, ___, and ___.",
    "This analysis demonstrates that …",
    "The answer to this question, I will suggest, is …",
    "Although ___ matters, it is ___ that best explains {topic}.",
    "The position taken here is that …",
    "In short, this essay argues for …",
  ],
  evidence: [
    "For example, …",
    "According to ___, …",
    "Evidence from ___ shows that …",
    "A study by ___ found that …",
    "Data from ___ indicate that …",
    "This is illustrated by …",
    "A clear example of this is …",
    "Consider the case of …",
    "In a survey of ___, …",
    "As ___ observes, “…”",
    "Figures published by ___ suggest that …",
    "Further support comes from …",
    "Records from ___ reveal that …",
    "The results of ___ point in the same direction: …",
    "In ___, for instance, …",
    "Recent findings on {topic} show that …",
    "This pattern is evident in …",
  ],
  explaining: [
    "This suggests that …",
    "This shows that …",
    "In other words, …",
    "The significance of this is that …",
    "This matters because …",
    "One explanation for this is that …",
    "What this reveals about {topic} is …",
    "The reason for this is …",
    "Taken together, these findings imply that …",
    "This example highlights …",
    "It follows that …",
    "The key point here is that …",
    "This can be explained by …",
    "As a result, …",
    "This is important for {topic} because …",
    "Put simply, …",
    "The effect of this is to …",
  ],
  comparing: [
    "Similarly, …",
    "Likewise, …",
    "In the same way, …",
    "Both ___ and ___ …",
    "Like ___, ___ also …",
    "By contrast, …",
    "In contrast to ___, …",
    "Whereas ___, …",
    "On the other hand, …",
    "Unlike ___, …",
    "While ___ emphasizes ___, ___ focuses on …",
    "The two approaches differ in …",
    "A key difference between ___ and ___ is …",
    "___ and ___ share …",
    "Compared with ___, {topic} …",
    "One similarity between ___ and ___ is …",
    "Despite their differences, ___ and ___ both …",
  ],
  conceding: [
    "Admittedly, …",
    "It is true that …",
    "Granted, …",
    "To be fair, …",
    "Critics of ___ have a point when they argue that …",
    "One could object that …",
    "This is not to say that …",
    "There are, of course, exceptions: …",
    "Some may argue that ___; however, …",
    "While it must be acknowledged that ___, …",
    "Even supporters of {topic} accept that …",
    "This view is not without its problems, since …",
    "Although this evidence is limited, …",
    "A reasonable objection is that …",
    "It would be wrong to suggest that …",
    "Opponents of {topic} point out that …",
    "Despite these concerns, …",
  ],
  emphasizing: [
    "Above all, …",
    "Most importantly, …",
    "It is worth stressing that …",
    "Crucially, …",
    "Indeed, …",
    "In fact, …",
    "Clearly, …",
    "What stands out most is …",
    "The most significant factor is …",
    "This point deserves particular attention: …",
    "Particularly important is …",
    "Especially striking is …",
    "Of all the factors, ___ matters most because …",
    "Notably, …",
    "Even more significant is …",
    "The single most important lesson of {topic} is …",
    "This is a critical point, because …",
  ],
  methods: [
    "This study used ___ to …",
    "Data were collected from …",
    "Participants were recruited through …",
    "A total of ___ participants took part in …",
    "The sample consisted of …",
    "To measure ___, we used …",
    "Interviews were conducted with …",
    "The survey included ___ items on …",
    "Responses were analyzed using …",
    "Each participant was asked to …",
    "The experiment was designed to test whether …",
    "Sources were selected according to the following criteria: …",
    "A ___ approach was chosen because …",
    "Ethical approval was obtained from …",
    "The procedure involved three stages: …",
    "To ensure reliability, …",
    "To investigate {topic}, the study …",
  ],
  results: [
    "The results show that …",
    "As Table ___ shows, …",
    "Figure ___ illustrates …",
    "A majority of participants (___%) …",
    "There was a significant difference between …",
    "No significant relationship was found between …",
    "The mean score for ___ was …",
    "Overall, the data indicate that …",
    "Contrary to expectations, …",
    "The most frequent response was …",
    "Scores increased from ___ to …",
    "The analysis revealed …",
    "Only ___ of the ___ participants …",
    "These results are consistent with …",
    "Interestingly, …",
    "The strongest effect was observed in …",
    "Results for {topic} were mixed: …",
  ],
  limitations: [
    "One limitation of this study is …",
    "These findings should be interpreted with caution because …",
    "The small sample size means that …",
    "Because the data were self-reported, …",
    "This study did not examine …",
    "It is possible that …",
    "The results may not generalize to …",
    "A further limitation concerns …",
    "Future research could address this by …",
    "This analysis relied on ___, which …",
    "The sources available on {topic} are limited to …",
    "Because the study was cross-sectional, …",
    "Several factors were beyond the scope of this essay, including …",
    "A longer time frame might have shown …",
    "The measures used may not fully capture …",
    "Further work is needed to establish whether …",
    "These conclusions about {topic} are therefore tentative, because …",
  ],
  concluding: [
    "In conclusion, …",
    "Overall, this essay has shown that …",
    "Taken together, the evidence suggests that …",
    "This analysis has demonstrated that …",
    "To sum up, …",
    "The evidence considered here points to …",
    "Ultimately, {topic} …",
    "What emerges from this discussion is …",
    "These findings have implications for …",
    "The broader lesson of {topic} is …",
    "Further research could explore …",
    "As this essay has argued, …",
    "In light of this evidence, …",
    "The key takeaway is that …",
    "If ___ is to change, …",
    "Looking ahead, …",
    "Returning to the question posed at the start, …",
  ],
};

const TOPIC_SLOT = "{topic}";

export interface StarterPart {
  text: string;
  /** True for the part that came from the reader's topic (or its stand-in). */
  slot: boolean;
}

/**
 * Split a starter around its topic slot and fill it: the reader's topic, lowercased
 * mid-sentence unless it reads as a name, capitalized when it opens the sentence — or
 * the stand-in ("[topic]") when no topic is given.
 */
export function fillStarter(
  starter: string,
  topic: string,
  standIn: string
): StarterPart[] {
  const cleaned = stripEnd(topic);
  const proper = properWords([cleaned]);
  const parts: StarterPart[] = [];
  const pieces = starter.split(TOPIC_SLOT);
  for (const [index, piece] of pieces.entries()) {
    if (index > 0) {
      const opens = parts.length === 0 || parts.at(-1)?.text === "";
      let value = standIn;
      if (cleaned !== "") {
        value = opens ? capitalizeFirst(cleaned) : lowerFirst(cleaned, proper);
      }
      parts.push({ text: value, slot: true });
    }
    parts.push({ text: piece, slot: false });
  }
  return parts.filter((part) => part.text !== "" || part.slot);
}

export function starterText(parts: readonly StarterPart[]): string {
  return parts.map((part) => part.text).join("");
}

export interface StarterGroup {
  fn: StarterFunction;
  starters: StarterPart[][];
}

/** The bank filtered by function and by a search over the filled-in text. */
export function filterStarters(
  bank: Readonly<Record<StarterFunction, readonly string[]>>,
  fn: StarterFunction | "all",
  query: string,
  topic: string,
  standIn: string
): StarterGroup[] {
  const needle = query.trim().toLowerCase();
  const functions = fn === "all" ? STARTER_FUNCTIONS : [fn];
  const groups: StarterGroup[] = [];
  for (const name of functions) {
    const starters = (bank[name] ?? [])
      .map((starter) => fillStarter(starter, topic, standIn))
      .filter(
        (parts) =>
          needle === "" || starterText(parts).toLowerCase().includes(needle)
      );
    if (starters.length > 0) {
      groups.push({ fn: name, starters });
    }
  }
  return groups;
}
